<template>
  <div class="d-flex flex-column">
    <div class="h5 mb-3">{{ planName }} Plan Terms and Conditions</div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Agreement Start Date</div>
      <div class="col text-muted small">
        This agreement is effective on the date of acceptance by the Member (Customer), and includes all Plans, Credits, and respective Pricing for selected Plans
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Term and End Date</div>
      <div class="col text-muted small">Term ending {{ term }} from the Agreement Start Date (except for Cycle Day and/or Status adjustments).</div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Billing Cycle Day</div>
      <div class="col text-muted small">
        Plan(s) are charged every {{ cycle }}(s) from the Agreement Start Date (except for Cycle Day and/or Status adjustments), and respective Credits are updated in your account
        once charge is processed successfully.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Payment Terms</div>
      <div class="col text-muted small">
        All payments are processed via the Debit Card, Credit Card, or ACH specified on the account. The first payment will be processed upon the launch of your Plan(s) and may
        include additional One-Time charges. All Plan's recurring fees are due and will be processed on the same date each {{ cycle }} as your Agreement Start Date ("Cycle Day")
        unless otherwise adjusted.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Initial Payment</div>
      <div class="col text-muted small">
        <b><Money-Format class="text-secondary" :value="priceCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format></b>
        plus applicable taxes. Due upon acceptance and launch of Plan(s). Includes any One-Time charges.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Recurring Fee</div>
      <div class="col text-muted small">
        <b><Money-Format class="text-secondary" :value="priceCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format></b>
        plus applicable taxes. Plan(s) fees each {{ cycle }} based on Plan(s) selected for the respoctive Agreement Period.
      </div>
    </div>
    <div v-if="downPaymentCents > 0" class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Down Payment</div>
      <div class="col text-muted small">
        <b><Money-Format class="text-secondary" :value="downPaymentCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format></b>
        plus applicable taxes. This plan requires a one time down payment due upon acceptance and launch of Plan(s).
      </div>
    </div>
    <div v-if="serviceFeeCents > 0" class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Service Fee</div>
      <div class="col text-muted small">
        <b><Money-Format class="text-secondary" :value="serviceFeeCents" :locale="locale" :currency-code="currency" :subunits-value="true"></Money-Format></b>
        <p>
          This plan includes a service fee of during each billing cycle.
        </p>
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Declined Payments</div>
      <div class="col text-muted small">
        A Declined charge will place your Plan(s) in "Past-Due" status and a $10 Declined fee may be charged each week until payment is successful. No Credits can be used while the
        Plan is in "Past-Due" status. Upon successful payment, outstanding charges will be processed and Credits will be applied to bring your account current.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Auto-Renewal Terms</div>
      <div class="col text-muted small">
        In order to avoid service disruption of your Plan(s), at the end of the Agreement Term this agreement will automatically renew for a period of another Agreement Term unless
        modified or cancelled by written notice (email or letter) from Member (Customer) to {{ computedBusinessName }} prior to the current Agreement End Date. Plan(s) fees may
        increase by up to 10% upon renewal without notification.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Non-Renewal Terms</div>
      <div class="col text-muted small">
        For Plan(s) cancelled at the end of their current Agreement Period, the Member will have 60 days from the current Agreement End Date to use any remaining Credits on their
        account for the respective Plan(s). Respective Credits are voided and the Plans(s) are terminated 60 days after the current Agreement End Date.
      </div>
    </div>
    <div class="d-flex row mb-2">
      <div class="col-3 text-secondary small">Early Termination</div>
      <div class="col text-muted small">
        For any termination by the Member before the current Agreement End Date, all remaining fees for the current Agreement Period will be charged in full (100%) and Credits will
        be applied to their account. The Member will have 60 days from the Early Termination End Date to use the respective Credits. The respective Credits are voided and the
        Plan(s) are terminated 60 days after the Early Termination End Date. The only exception is, when documented, that the member is moving more than 50 miles away from the
        nearest location.
      </div>
    </div>
    <div class="d-flex mt-3 justify-content-begin">Accepted on: {{ formattedStartDate }}</div>
  </div>
</template>
<script>
import MoneyFormat from "vue-money-format";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "UserBusinessPlanAgreement",
  components: { MoneyFormat },
  props: {
    userId: String,
    userBusinessPlanId: String,
    planName: String,
    priceCents: Number,
    currency: String,
    businessName: String,
    termLength: Number,
    termInterval: String,
    cycleFrequency: Number,
    cycleInterval: String,
    startDate: String,
    downPaymentCents: Number,
    serviceFeeCents: Number,
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    computedBusinessName() {
      if (this.businessName) return this.businessName;
      else {
        if (this.organization.type === "business") return this.organization.description;
        else return this.businessName;
      }
    },
    term() {
      return this.termLength > 1 ? this.termLength + " " + this.termInterval + "s" : this.termLength + " " + this.termInterval;
    },
    cycle() {
      return this.cycleFrequency > 1 ? this.cycleFrequency + " " + this.cycleInterval + "s" : this.cycleFrequency + " " + this.cycleInterval;
    },
    locale() {
      return window.navigator.language;
    },
    formattedStartDate() {
      return moment
        .utc(this.startDate)
        .local()
        .format("MMM DD, YYYY h:mm a");
    },
  },
};
</script>
